import { createSlice } from "@reduxjs/toolkit";
import {
  loadCourseLessons,
  loadLesson,
  updateLessonProgress,
  checkCertificateEligibility,
  completeLesson
} from "./asyncThunks";
import { LOGOUT } from "../../../utils/constants";

const initialState = {
  // Course lessons data
  currentCourseId: null,
  lessons: [],
  lessonsById: {},
  courseData: null, // Course information including PDF scripts
  
  // Current lesson data
  currentLessonId: null,
  currentLesson: null,
  
  // Progress tracking
  localProgress: {}, // Local progress before server sync
  
  // Certificate eligibility
  certificateEligibility: {},
  
  // Loading states
  loading: {
    courseLessons: false,
    lesson: false,
    progress: false,
    certificate: false
  },
  
  // Error states
  errors: {
    courseLessons: null,
    lesson: null,
    progress: null,
    certificate: null
  },
  
  // UI states
  canApplyForCertificate: false,
  courseCompleted: false,
  accessExpired: false,
  accessExpiryDate: null,
  accessDenied: false,
  lastUpdate: null
};

const lessonProgressSlice = createSlice({
  name: "lessonProgress",
  initialState,
  reducers: {
    // Set current lesson
    setCurrentLesson: (state, action) => {
      state.currentLessonId = action.payload;
      state.currentLesson = state.lessonsById[action.payload] || null;
    },
    
    // Update local progress (before server sync)
    updateLocalProgress: (state, action) => {
      const { lessonId, progress } = action.payload;
      state.localProgress[lessonId] = {
        ...state.localProgress[lessonId],
        ...progress,
        lastUpdate: Date.now()
      };
      
      // Update lesson in lessonsById if it exists
      if (state.lessonsById[lessonId]) {
        state.lessonsById[lessonId].progress = {
          ...state.lessonsById[lessonId].progress,
          ...progress
        };
      }
      
      state.lastUpdate = Date.now();
    },
    
    // Clear local progress for a lesson (after successful server sync)
    clearLocalProgress: (state, action) => {
      const lessonId = action.payload;
      delete state.localProgress[lessonId];
    },
    
    // Clear all data (on logout or course change)
    clearAllData: (state) => {
      return { ...initialState };
    },
    
    // Clear errors
    clearErrors: (state) => {
      state.errors = {
        courseLessons: null,
        lesson: null,
        progress: null,
        certificate: null
      };
    },
    
    // Set course completion status
    setCourseCompleted: (state, action) => {
      state.courseCompleted = action.payload;
    },
    
    // Set certificate eligibility
    setCertificateEligibility: (state, action) => {
      const { courseId, canApply } = action.payload;
      state.certificateEligibility[courseId] = canApply;
      state.canApplyForCertificate = canApply;
    }
  },
  extraReducers: (builder) => {
    builder
      // Load course lessons
      .addCase(loadCourseLessons.pending, (state, action) => {
        state.loading.courseLessons = true;
        state.errors.courseLessons = null;
        // Zapamti poslednji traženi kurs da odgovori van redosleda
        // ne bi pregazili podatke tekućeg kursa
        state.currentCourseId = action.meta.arg;
      })
      .addCase(loadCourseLessons.fulfilled, (state, action) => {
        if (action.meta.arg !== state.currentCourseId) {
          return;
        }
        state.loading.courseLessons = false;
        state.lessons = action.payload.lessons || [];
        const course = action.payload.course || null;
        if (course && typeof course.pdfScripts === "string") {
          try { course.pdfScripts = JSON.parse(course.pdfScripts); } catch { course.pdfScripts = []; }
        }
        state.courseData = course;
        
        // Create lessons by ID map
        state.lessonsById = {};
        state.lessons.forEach(lesson => {
          state.lessonsById[lesson.id] = lesson;
        });
        
        state.lastUpdate = Date.now();
      })
      .addCase(loadCourseLessons.rejected, (state, action) => {
        if (action.meta.arg !== state.currentCourseId) {
          return;
        }
        state.loading.courseLessons = false;
        if (action.payload?.expired) {
          state.accessExpired = true;
          state.accessExpiryDate = action.payload.expiryDate;
          state.errors.courseLessons = action.payload.message;
        } else if (action.payload?.accessDenied) {
          state.accessDenied = true;
          state.errors.courseLessons = action.payload.message;
        } else {
          state.errors.courseLessons = action.payload;
        }
      })
      
      // Load single lesson
      .addCase(loadLesson.pending, (state, action) => {
        state.loading.lesson = true;
        state.errors.lesson = null;
        // Zapamti poslednju traženu lekciju da odgovori van redosleda
        // ne bi pregazili tekuću lekciju
        state.currentLessonId = action.meta.arg;
      })
      .addCase(loadLesson.fulfilled, (state, action) => {
        state.loading.lesson = false;
        const lesson = action.payload.lesson;

        // Update current lesson only if this is still the latest requested one
        if (lesson.id === state.currentLessonId) {
          state.currentLesson = lesson;
        }

        // Update in lessonsById
        state.lessonsById[lesson.id] = lesson;
        
        // Update in lessons array if it exists, preserving existing isUnlocked if not provided
        const lessonIndex = state.lessons.findIndex(l => l.id === lesson.id);
        if (lessonIndex !== -1) {
          state.lessons[lessonIndex] = {
            ...lesson,
            isUnlocked: lesson.isUnlocked !== undefined ? lesson.isUnlocked : state.lessons[lessonIndex].isUnlocked
          };
        }
        
        state.lastUpdate = Date.now();
      })
      .addCase(loadLesson.rejected, (state, action) => {
        state.loading.lesson = false;
        state.errors.lesson = action.payload;
      })
      
      // Update lesson progress
      .addCase(updateLessonProgress.pending, (state) => {
        state.loading.progress = true;
        state.errors.progress = null;
      })
      .addCase(updateLessonProgress.fulfilled, (state, action) => {
        state.loading.progress = false;
        const { lessonId, progress, courseCompleted } = action.payload;
        
        // Clear local progress since it's now synced
        delete state.localProgress[lessonId];
        
        // Update lesson progress in all relevant places
        if (state.lessonsById[lessonId]) {
          state.lessonsById[lessonId].progress = {
            ...state.lessonsById[lessonId].progress,
            ...progress
          };
        }
        
        // Update current lesson if it matches
        if (state.currentLessonId === lessonId && state.currentLesson) {
          state.currentLesson.progress = {
            ...state.currentLesson.progress,
            ...progress
          };
        }
        
        // Update in lessons array
        const lessonIndex = state.lessons.findIndex(l => l.id === lessonId);
        if (lessonIndex !== -1) {
          state.lessons[lessonIndex].progress = {
            ...state.lessons[lessonIndex].progress,
            ...progress
          };
        }
        
        // Update course completion status
        if (courseCompleted !== undefined) {
          state.courseCompleted = courseCompleted;
          if (courseCompleted && state.currentCourseId) {
            // Server javlja i da li kurs uopšte izdaje sertifikat (mini
            // kursevi ne izdaju) — fallback na true za stariji odgovor
            const canApply =
              action.payload.canApplyForCertificate !== undefined
                ? action.payload.canApplyForCertificate
                : true;
            state.certificateEligibility[state.currentCourseId] = canApply;
            state.canApplyForCertificate = canApply;
          }
        }

        state.lastUpdate = Date.now();
      })
      .addCase(updateLessonProgress.rejected, (state, action) => {
        state.loading.progress = false;
        state.errors.progress = action.payload;
      })
      
      // Complete lesson
      .addCase(completeLesson.pending, (state) => {
        state.loading.progress = true;
      })
      .addCase(completeLesson.fulfilled, (state, action) => {
        state.loading.progress = false;
        const { lessonId, progress, courseCompleted } = action.payload;
        const completedProgress = {
          ...progress,
          isCompleted: true,
          progressPercentage: progress?.progressPercentage ?? 100
        };

        // Clear local progress since it's now synced
        delete state.localProgress[lessonId];

        // Mark lesson as completed
        if (state.lessonsById[lessonId]) {
          state.lessonsById[lessonId].progress = {
            ...state.lessonsById[lessonId].progress,
            ...completedProgress
          };
        }

        // Update current lesson if it matches
        if (state.currentLessonId === lessonId && state.currentLesson) {
          state.currentLesson.progress = {
            ...state.currentLesson.progress,
            ...completedProgress
          };
        }

        // Update in lessons array
        const completedLessonIndex = state.lessons.findIndex(l => l.id === lessonId);
        if (completedLessonIndex !== -1) {
          state.lessons[completedLessonIndex].progress = {
            ...state.lessons[completedLessonIndex].progress,
            ...completedProgress
          };
        }

        // Update course completion
        if (courseCompleted) {
          state.courseCompleted = true;
          if (state.currentCourseId) {
            // Isto kao kod updateLessonProgress — poštuj serverski flag
            const canApply =
              action.payload.canApplyForCertificate !== undefined
                ? action.payload.canApplyForCertificate
                : true;
            state.certificateEligibility[state.currentCourseId] = canApply;
            state.canApplyForCertificate = canApply;
          }
        }
        
        state.lastUpdate = Date.now();
      })
      .addCase(completeLesson.rejected, (state, action) => {
        state.loading.progress = false;
        state.errors.progress = action.payload;
      })
      
      // Check certificate eligibility
      .addCase(checkCertificateEligibility.pending, (state) => {
        state.loading.certificate = true;
        state.errors.certificate = null;
      })
      .addCase(checkCertificateEligibility.fulfilled, (state, action) => {
        state.loading.certificate = false;
        const { courseId, canApply, courseCompleted } = action.payload;
        
        state.certificateEligibility[courseId] = canApply;
        if (courseId === state.currentCourseId) {
          state.canApplyForCertificate = canApply;
          state.courseCompleted = courseCompleted || false;
        }
      })
      .addCase(checkCertificateEligibility.rejected, (state, action) => {
        state.loading.certificate = false;
        state.errors.certificate = action.payload;
      });

    builder.addCase(LOGOUT, () => initialState);
  }
});

export const {
  setCurrentLesson,
  updateLocalProgress,
  clearLocalProgress,
  clearAllData,
  clearErrors,
  setCourseCompleted,
  setCertificateEligibility
} = lessonProgressSlice.actions;

// Selectors
export const selectLessons = (state) => state.lessonProgress.lessons;
export const selectLessonsById = (state) => state.lessonProgress.lessonsById;
export const selectCurrentLesson = (state) => state.lessonProgress.currentLesson;
export const selectCurrentLessonId = (state) => state.lessonProgress.currentLessonId;
export const selectCurrentCourseId = (state) => state.lessonProgress.currentCourseId;
export const selectCourseData = (state) => state.lessonProgress.courseData;
export const selectLocalProgress = (state) => state.lessonProgress.localProgress;
export const selectCourseCompleted = (state) => state.lessonProgress.courseCompleted;
export const selectCanApplyForCertificate = (state) => state.lessonProgress.canApplyForCertificate;
export const selectCertificateEligibility = (state) => state.lessonProgress.certificateEligibility;
export const selectAccessExpired = (state) => state.lessonProgress.accessExpired;
export const selectAccessExpiryDate = (state) => state.lessonProgress.accessExpiryDate;
export const selectAccessDenied = (state) => state.lessonProgress.accessDenied;
export const selectLastUpdate = (state) => state.lessonProgress.lastUpdate;

// Loading selectors
export const selectLoading = (state) => state.lessonProgress.loading;
export const selectCourseLessonsLoading = (state) => state.lessonProgress.loading.courseLessons;
export const selectLessonLoading = (state) => state.lessonProgress.loading.lesson;
export const selectProgressLoading = (state) => state.lessonProgress.loading.progress;
export const selectCertificateLoading = (state) => state.lessonProgress.loading.certificate;

// Error selectors
export const selectErrors = (state) => state.lessonProgress.errors;
export const selectCourseLessonsError = (state) => state.lessonProgress.errors.courseLessons;
export const selectLessonError = (state) => state.lessonProgress.errors.lesson;
export const selectProgressError = (state) => state.lessonProgress.errors.progress;
export const selectCertificateError = (state) => state.lessonProgress.errors.certificate;

// Derived selectors
export const selectLessonById = (lessonId) => (state) => 
  state.lessonProgress.lessonsById[lessonId] || null;

export const selectLessonProgress = (lessonId) => (state) => {
  const lesson = state.lessonProgress.lessonsById[lessonId];
  const localProgress = state.lessonProgress.localProgress[lessonId];
  
  return {
    ...lesson?.progress,
    ...localProgress
  };
};

export const selectUnlockedLessons = (state) => 
  state.lessonProgress.lessons.filter(lesson => lesson.isUnlocked);

export const selectCompletedLessons = (state) => 
  state.lessonProgress.lessons.filter(lesson => lesson.progress?.isCompleted);

export const selectNextLesson = (state) => {
  const lessons = state.lessonProgress.lessons;
  return lessons.find(lesson => 
    lesson.isUnlocked && !lesson.progress?.isCompleted
  ) || null;
};

export default lessonProgressSlice.reducer;